import { onAuthStateChanged } from "firebase/auth";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { auth, firestore, realtime, storage } from "../firebase-config";
import { AppMode, dark_mode_primary, dark_mode_secondary } from "./values";
import { collection, getDocs, getDoc, doc, where } from "firebase/firestore";
import { getDownloadURL, uploadBytes, ref as storageRef } from "firebase/storage";
import { get, onValue, orderByChild, push, query, ref, set, update, equalTo } from "firebase/database";
import { ConfirmDialog, EmptyChat, EmptyMessagesList, LoadingScreen, MessageContextMenu, ProfileOptionButton } from "./widgets";

function getUserChats(userId, callback) {
    if (!userId) return () => { };
    const userChatsRef = ref(realtime, 'chats');
    ensureUserInRealtimeDB(userId).catch(() => { });
    return onValue(
        query(userChatsRef, orderByChild(`participants/${userId}`), equalTo(true)),
        async (snapshot) => {
            const chats = [];
            if (snapshot.exists()) {
                const chatData = snapshot.val();
                for (const chatId in chatData) {
                    const chat = chatData[chatId];
                    const participants = Object.keys(chat.participants || {});
                    const otherUserId = participants.find(id => id !== userId);
                    if (!otherUserId) {
                        continue;
                    }
                    try {
                        const otherUserData = await ensureUserInRealtimeDB(otherUserId);
                        if (!otherUserData) {
                            continue;
                        }
                        chats.push({
                            id: chatId,
                            uid: otherUserId,
                            name: otherUserData.displayName || 'User',
                            imageUrl: otherUserData.photoURL || 'https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg',
                            message: chat.lastMessage || 'No messages yet',
                            time: chat.lastMessageAt ? new Date(chat.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
                            read: chat.read?.[userId] || false,
                        });
                    }
                    catch (error) { }
                }
                chats.sort((a, b) => {
                    const timeA = chatData[a.id]?.lastMessageAt || 0;
                    const timeB = chatData[b.id]?.lastMessageAt || 0;
                    return timeB - timeA;
                });
            }
            else { }
            callback(chats);
        },
        () => {
            callback([]);
        }
    );
}

async function ensureUserInRealtimeDB(userId) {
    const userRef = ref(realtime, `chatUsers/${userId}`);
    const snapshot = await get(userRef);
    if (snapshot.exists() && snapshot.hasChild("uid")) {
        return snapshot.val();
    }

    const usersRef = collection(firestore, 'users');
    const q = query(usersRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const realtimeUserData = {
            uid: userData.userId,
            displayName: userData.displayName || 'User',
            email: userData.email || '',
            photoURL: userData.photoURL || '',
            lastActive: Date.now()
        };
        await set(ref(realtime, `chatUsers/${userData.userId}`), realtimeUserData);
        return realtimeUserData;
    }

    const directDoc = await getDoc(doc(firestore, 'users', userId));
    if (directDoc.exists()) {
        const userData = directDoc.data();
        const uidToUse = userData.userId || userId;
        const realtimeUserData = {
            uid: uidToUse,
            displayName: userData.displayName || 'User',
            email: userData.email || '',
            photoURL: userData.photoURL || '',
            lastActive: Date.now()
        };
        await set(ref(realtime, `chatUsers/${uidToUse}`), realtimeUserData);
        return realtimeUserData;
    }
    return null;
}

function useUserChats(userId) {
    const [chats, setChats] = useState([]);
    useEffect(() => {
        if (!userId) {
            setChats([]);
            return;
        }
        const unsubscribe = getUserChats(userId, setChats);
        return () => unsubscribe();
    }, [userId]);

    return chats;
}

function useMessages(chatId) {
    const [error, setError] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!chatId) {
            setMessages([]);
            setLoading(false);
            return () => { };
        }
        setLoading(true);
        try {
            const messagesRef = ref(realtime, `chats/${chatId}/messages`);
            const unsubscribe = onValue(
                query(messagesRef),
                (snapshot) => {
                    const newMessages = [];
                    if (snapshot.exists()) {
                        const messagesData = snapshot.val();
                        Object.keys(messagesData).forEach(key => {
                            newMessages.push({
                                id: key,
                                ...messagesData[key],
                                timeStamp: parseInt(messagesData[key].timeStamp) || Date.now(),
                            });
                        });
                        newMessages.sort((a, b) => {
                            const timeA = parseInt(a.timeStamp || '0');
                            const timeB = parseInt(b.timeStamp || '0');
                            return timeA - timeB;
                        });
                    }
                    else { }
                    setMessages(newMessages);
                    setLoading(false);
                    setError(null);
                },
                (err) => {
                    setError(err);
                    setLoading(false);
                }
            );
            return () => unsubscribe();
        } catch (err) {
            setError(err);
            setLoading(false);
            return () => { };
        }
    }, [chatId]);
    return { messages, loading, error };
}

function useNewMessageNotifications(userId) {
    const [notifications, setNotifications] = useState([]);
    useEffect(() => {
        if (!userId) {
            setNotifications([]);
            return () => { };
        }
        const chatsRef = ref(realtime, 'chats');
        const unsubscribe = onValue(
            query(chatsRef, orderByChild(`participants/${userId}`), equalTo(true)),
            async (snapshot) => {
                const newNotifications = [];
                if (snapshot.exists()) {
                    const chatsData = snapshot.val();
                    for (const chatId in chatsData) {
                        const chat = chatsData[chatId];
                        if (chat.read && chat.read[userId] === false) {
                            const senderId = chat.lastSenderId;
                            if (senderId === userId) continue;
                            try {
                                const senderSnapshot = await get(ref(realtime, `chatUsers/${senderId}`));
                                const senderData = senderSnapshot.val() || {};
                                newNotifications.push({
                                    chatId: chatId,
                                    message: chat.lastMessage || 'New message',
                                    senderName: senderData.displayName || 'User',
                                    timeStamp: chat.lastMessageAt,
                                });
                            } catch (error) {
                                newNotifications.push({
                                    chatId: chatId,
                                    message: chat.lastMessage || 'New message',
                                    senderName: 'User',
                                    timeStamp: chat.lastMessageAt,
                                });
                            }
                        }
                    }
                }
                setNotifications(newNotifications);
            },
            () => {
                setNotifications([]);
            }
        );
        return () => unsubscribe();
    }, [userId]);
    return notifications;
}

export function Messages({ appMode }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, user => {
            if (user) {
                setCurrentUser(user);
                ensureUserInRealtimeDB(user.uid).catch(() => { });
            } else {
                setCurrentUser(null);
            }
            setAuthLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const messageList = useUserChats(currentUser?.uid);
    if (authLoading) {
        return <LoadingScreen appMode={appMode} />;
    }
    return <VerticalTabs appMode={appMode} currentUser={currentUser} messages={messageList} user={currentUser} />;
}

const VerticalTabs = ({ appMode, messages, user }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const notifications = useNewMessageNotifications(user?.uid);
    const [showNewChatPopup, setShowNewChatPopup] = useState(false);

    const handleClick = (index) => setActiveTab(index);

    const unreadCounts = useMemo(() => {
        const counts = {};
        notifications.forEach(notification => {
            counts[notification.chatId] = (counts[notification.chatId] || 0) + 1;
        });
        return counts;
    }, [notifications]);

    const toggleNewChatPopup = () => {
        setShowNewChatPopup(!showNewChatPopup);
        setUserSearchQuery('');
        setSearchResults([]);
    };

    const searchUsersByEmail = async (email) => {
        if (!email || email.trim() === '' || !user) return;
        try {
            const usersRef = collection(firestore, 'users');
            const querySnapshot = await getDocs(usersRef);
            const results = [];
            querySnapshot.forEach((doc) => {
                const userData = doc.data();
                if (userData.email && userData.email.toLowerCase().startsWith(email.toLowerCase()) && userData.userId !== user.uid) {
                    results.push({
                        uid: userData.userId,
                        ...userData,
                    });
                }
            });
            setSearchResults(results);
        } catch (error) {
            setSearchResults([]);
        }
    };

    function generateChatId(userId1, userId2) {
        return [userId1, userId2].sort().join('_');
    }

    const startNewChat = async (selectedUser) => {
        if (!user || !user.uid) {
            return;
        }

        if (!selectedUser || !selectedUser.uid) {
            return;
        }

        try {
            await ensureUserInRealtimeDB(user.uid);
            await ensureUserInRealtimeDB(selectedUser.uid);
            const chatId = generateChatId(user.uid, selectedUser.uid);
            const chatRef = ref(realtime, `chats/${chatId}`);
            const chatSnapshot = await get(chatRef);
            if (!chatSnapshot.exists()) {
                const chatData = {
                    participants: {
                        [user.uid]: true,
                        [selectedUser.uid]: true,
                    },
                    createdAt: Date.now(),
                    lastMessageAt: Date.now(),
                    read: {
                        [user.uid]: true,
                        [selectedUser.uid]: true,
                    }
                };
                await set(chatRef, chatData);
            }
            else { }
            toggleNewChatPopup();
            return chatId;
        } catch (error) {
            return null;
        }
    };

    const filteredMessages = messages.filter(
        (message) =>
            message?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            message?.message?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="vertical-tabs flex h-[100vh]">
            <div className={`${appMode === AppMode.LIGHT ? 'bg-white text-[#8E92BC] border-r border-gray-200 shadow-sm' : dark_mode_primary + ' border-r border-gray-800'} w-1/4 h-full flex flex-col justify-between transition-all duration-300 ease-in-out`}>
                <div className={`py-6 px-5 flex-col ${appMode === AppMode.LIGHT ? 'text-gray-800 border-b border-gray-100' : 'text-white border-b border-gray-800'}`}>
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-xl font-bold">Messages</h1>
                        <button onClick={toggleNewChatPopup} className={`p-2 rounded-full ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-200'} transition-colors duration-200`} title="New Chat">
                            <i className="bi bi-plus-lg"></i>
                        </button>
                    </div>
                    <div className="relative">
                        <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        <input className={`w-full h-10 pl-10 pr-4 rounded-lg bg-transparent ${appMode === AppMode.LIGHT ? 'border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100' : 'border border-gray-700 focus:border-blue-600 focus:ring-2 focus:ring-blue-900'} focus:outline-none transition-all duration-200`} placeholder="Search messages..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    </div>
                </div>
                <div className='flex-1 overflow-y-auto'>
                    {filteredMessages.length > 0
                        ? filteredMessages.map((message, index) => (
                            <React.Fragment key={message.id || index}>
                                <div className={`flex justify-between items-center py-3 px-5 cursor-pointer transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'text-gray-800 hover:bg-gray-50' : 'text-white hover:bg-gray-800'} ${index === activeTab ? (appMode === AppMode.LIGHT ? 'bg-blue-50 border-l-4 border-blue-500' : dark_mode_secondary + ' border-l-4 border-blue-500') : ''}`} onClick={() => handleClick(index)}>
                                    <div className="w-1/5 overflow-hidden flex-shrink-0">
                                        <div className={`h-12 w-12 rounded-full overflow-hidden border-2 ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-700'} shadow-sm`}>
                                            <img src={message.imageUrl} className='h-full w-full object-cover' alt='' />
                                        </div>
                                    </div>
                                    <div className="w-3/5 flex-col flex-grow px-3 overflow-hidden">
                                        <h1 className={`${index === activeTab ? 'font-semibold' : 'font-medium'} truncate max-w-[120px] capitalize`}>{message.name}</h1>
                                        <h2 className={`py-1 text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'} truncate max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap`}>{message.message}</h2>
                                    </div>
                                    <div className="flex-col text-end">
                                        <h2 className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{message.time}</h2>
                                        {message.read
                                            ? <i className="bi bi-check2-all text-blue-500"></i>
                                            : <div className="relative">
                                                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                                {unreadCounts[message.id] > 1 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center shadow-sm">{unreadCounts[message.id]}</span>}
                                            </div>
                                        }
                                    </div>
                                </div>
                                <hr className={`mx-4 ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'}`} />
                            </React.Fragment>
                        ))
                        : <EmptyMessagesList appMode={appMode} toggleNewChatPopup={toggleNewChatPopup} />
                    }
                </div>
            </div>
            <div className="flex-grow">
                {filteredMessages.length > 0 && activeTab < filteredMessages.length
                    ? <ChatScreen selectedTab={activeTab} imageUrl={filteredMessages[activeTab]?.imageUrl} appMode={appMode} user={user} name={filteredMessages[activeTab]?.name} chatId={filteredMessages[activeTab]?.id} />
                    : <EmptyChat appMode={appMode} toggleNewChatPopup={toggleNewChatPopup} />
                }
            </div>
            {showNewChatPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className={`rounded-lg p-6 w-96 ${appMode === AppMode.LIGHT ? 'bg-white text-gray-800 border border-gray-200' : dark_mode_secondary + ' text-white border border-gray-700'} shadow-xl transition-all duration-300`}>
                        <div className={`flex justify-between items-center mb-5 pb-3 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                            <h2 className="text-xl font-bold">New Chat</h2>
                            <button onClick={toggleNewChatPopup} className={`p-2 rounded-full ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700' : 'text-gray-400 hover:bg-gray-700 hover:text-white'} transition-colors duration-200`}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="mb-4">
                            <div className="relative mb-2">
                                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                                <input type="text" className={`w-full pl-10 pr-4 py-3 border rounded-lg ${appMode === AppMode.LIGHT ? 'border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100' : 'border-gray-700 bg-gray-800 focus:border-blue-600 focus:ring-2 focus:ring-blue-900'} focus:outline-none transition-all duration-200`} placeholder="Search by email" value={userSearchQuery} onChange={(e) => setUserSearchQuery(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && searchUsersByEmail(userSearchQuery)} />
                            </div>
                            <button className={`w-full py-2.5 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-700 hover:bg-blue-800'} text-white shadow-sm hover:shadow`} onClick={() => searchUsersByEmail(userSearchQuery)}>Search</button>
                        </div>
                        <div className={`max-h-60 overflow-y-auto rounded-lg ${appMode === AppMode.LIGHT ? 'border border-gray-200' : 'border border-gray-700'}`}>
                            {searchResults.length > 0
                                ? searchResults.map((userResult) => (
                                    <div key={userResult.uid} className={`p-3 ${appMode === AppMode.LIGHT ? 'border-b border-gray-200 hover:bg-gray-50' : 'border-b border-gray-700 hover:bg-gray-800'} cursor-pointer flex items-center transition-colors duration-200`} onClick={() => startNewChat(userResult)}>
                                        <div className={`h-10 w-10 rounded-full overflow-hidden mr-3 border-2 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'} shadow-sm`}>
                                            <img src={userResult.photoURL || 'https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg'} className="w-full h-full object-cover" alt="" />
                                        </div>
                                        <div>
                                            <p className="font-semibold capitalize">{userResult.displayName}</p>
                                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{userResult.email}</p>
                                        </div>
                                    </div>
                                ))
                                : userSearchQuery
                                    ? <p className={`text-center ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'} py-4`}>No users found</p>
                                    : null
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function ChatScreen({ selectedTab, imageUrl, appMode, user, name, chatId }) {
    const messagesEndRef = useRef(null);
    const [value, setValue] = React.useState('');
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [attachMessage, setAttachMessage] = useState('');
    const { messages, loading, error } = useMessages(chatId);
    const [editingMessage, setEditingMessage] = useState(null);
    const [messageToAction, setMessageToAction] = useState(null);
    const [showAttachPopup, setShowAttachPopup] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, messageId: null });

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        if (!chatId || !user?.uid) return;
        const markAsRead = async () => {
            try {
                await update(ref(realtime, `chats/${chatId}`), {
                    [`read/${user.uid}`]: true
                });
            } catch (error) { }
        };
        markAsRead();
        const interval = setInterval(markAsRead, 5000);
        return () => clearInterval(interval);
    }, [chatId, user]);

    async function sendMessage(chatId, user, text, fileUrl = '', fileName = '') {
        if (!text.trim() || !user?.uid || !chatId) {
            return null;
        }
        try {
            const chatRef = ref(realtime, `chats/${chatId}`);
            const chatSnapshot = await get(chatRef);
            if (!chatSnapshot.exists()) {
                return null;
            }

            const chatData = chatSnapshot.val();
            if (!chatData.participants || !chatData.participants[user.uid]) {
                return null;
            }

            const participants = Object.keys(chatData.participants);
            const otherUserId = participants.find(id => id !== user.uid);
            if (!otherUserId) {
                return null;
            }
            const newMessageRef = push(ref(realtime, `chats/${chatId}/messages`));
            const messageData = {
                sender_id: user.uid,
                senderName: user.displayName || 'User',
                message: text.trim(),
                receiverId: otherUserId,
                timeStamp: Date.now().toString(),
                created_at: new Date().toISOString(),
                seen: false,
                key: newMessageRef.key,
                fileUrl: fileUrl || null,
                fileName: fileName || null,
            };
            await set(newMessageRef, messageData);
            const updates = {
                lastMessage: text.trim(),
                lastMessageAt: Date.now(),
                lastSenderId: user.uid,
                [`read/${otherUserId}`]: false,
                [`read/${user.uid}`]: true,
            };
            await update(chatRef, updates);
            await set(ref(realtime, `chatUsers/${user.uid}/${otherUserId}`), {
                ...messageData,
                seen: true,
            });
            await set(ref(realtime, `chatUsers/${otherUserId}/${user.uid}`), {
                ...messageData,
                seen: false,
            });
            return newMessageRef.key;
        } catch (error) {
            return null;
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!value.trim() || sending) return;
        if (editingMessage) {
            setSending(true);
            try {
                const messageRef = ref(realtime, `chats/${chatId}/messages/${editingMessage.id}`);
                await update(messageRef, {
                    message: value.trim(),
                    edited: true,
                    editedAt: Date.now().toString()
                });
                const chatRef = ref(realtime, `chats/${chatId}`);
                const chatSnapshot = await get(chatRef);
                if (chatSnapshot.exists()) {
                    const chatData = chatSnapshot.val();
                    if (chatData.lastMessageAt === Number(editingMessage.timeStamp)) {
                        await update(chatRef, { lastMessage: value.trim() });
                    }
                }
                setEditingMessage(null);
            } catch (error) {
                alert("Failed to edit message. Please try again.");
            } finally {
                setSending(false);
            }
        } else {
            setSending(true);
            try {
                const chatRef = ref(realtime, `chats/${chatId}`);
                const chatSnapshot = await get(chatRef);
                if (!chatSnapshot.exists()) {
                    return;
                }
                const chatData = chatSnapshot.val();
                const participants = Object.keys(chatData.participants || {});
                const recipientId = participants.find(id => id !== user.uid);
                if (!recipientId) {
                    return;
                }
                await sendMessage(chatId, user, value);
            } catch (err) {
                alert("Failed to send message. Please try again.");
            } finally {
                setSending(false);
            }
        }
        setValue('');
    };

    const handleContextMenu = (e, message) => {
        if (message.sender_id === user?.uid) {
            e.preventDefault();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const menuWidth = 180;
            const menuHeight = 100;
            let x = e.pageX + 10;
            let y = e.pageY - 10;
            if (x + menuWidth > viewportWidth) {
                x = e.pageX - menuWidth - 10;
            }
            if (y + menuHeight > viewportHeight) {
                y = viewportHeight - menuHeight - 10;
            }
            if (y < 10) y = 10;
            setContextMenu({
                visible: true,
                x: x,
                y: y,
                messageId: message.id,
                message: message,
            });
        }
    };

    const handleCloseContextMenu = () => {
        setContextMenu({ visible: false, x: 0, y: 0, messageId: null });
    };

    const handleEditMessage = () => {
        const messageToEdit = contextMenu.message;
        setEditingMessage(messageToEdit);
        setValue(messageToEdit.message);
        handleCloseContextMenu();
        document.querySelector('input[type="text"]').focus();
    };

    const handleConfirmDelete = async () => {
        if (!messageToAction) {
            setShowDeleteConfirm(false);
            return;
        }
        try {
            const messageRef = ref(realtime, `chats/${chatId}/messages/${messageToAction.id}`);
            const messageSnapshot = await get(messageRef);
            if (!messageSnapshot.exists()) {
                setShowDeleteConfirm(false);
                setMessageToAction(null);
                return;
            }
            await update(messageRef, { [`deletedFor/${user.uid}`]: true });
            const chatRef = ref(realtime, `chats/${chatId}`);
            const chatSnapshot = await get(chatRef);
            if (chatSnapshot.exists()) {
                const chatData = chatSnapshot.val();
                if (chatData.lastMessageAt === parseInt(messageSnapshot.val().timeStamp)) {
                    const messagesRef = ref(realtime, `chats/${chatId}/messages`);
                    const messagesSnapshot = await get(messagesRef);
                    if (messagesSnapshot.exists()) {
                        const messagesData = messagesSnapshot.val();
                        let lastMessage = null;
                        let lastMessageTime = 0;
                        Object.entries(messagesData).forEach(([msgId, msg]) => {
                            if (msgId !== messageToAction.id && parseInt(msg.timeStamp) > lastMessageTime) {
                                lastMessage = msg;
                                lastMessageTime = parseInt(msg.timeStamp);
                            }
                        });

                        if (lastMessage) {
                            await update(chatRef, {
                                lastMessage: lastMessage.message,
                                lastMessageAt: parseInt(lastMessage.timeStamp),
                                lastSenderId: lastMessage.sender_id,
                            });
                        }
                    }
                }
            }
            setShowDeleteConfirm(false);
            setMessageToAction(null);
        } catch (error) {
            alert("Failed to delete message. Please try again.");
            setShowDeleteConfirm(false);
            setMessageToAction(null);
        }
    };
    const handleDeleteMessage = () => {
        setMessageToAction(contextMenu.message);
        setShowDeleteConfirm(true);
        handleCloseContextMenu();
    };

    const handleMessageReply = () => { }

    const messageReplySuggestion = () => { }

    const formattedMessages = messages
        .filter(msg => !msg.deletedFor || !msg.deletedFor[user?.uid])
        .map(msg => {
            const isCurrentUser = msg.sender_id === user?.uid;
            return {
                id: msg.id,
                message: msg.message || '',
                time: msg.timeStamp ?
                    new Date(parseInt(msg.timeStamp)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                    '',
                status: msg.seen ? 'read' : 'sent',
                myMessage: isCurrentUser,
                edited: msg.edited,
                sender_id: msg.sender_id,
                timeStamp: msg.timeStamp,
                fileUrl: msg.fileUrl || null,
                fileName: msg.fileName || null,
            };
        });

    if (selectedTab == null) {
        return <EmptyChat appMode={appMode} />;
    }
    else {
        return (
            <div className="chat h-screen flex flex-col">
                <div className={`p-5 ${appMode === AppMode.LIGHT ? 'bg-white border-b border-gray-200 shadow-sm' : dark_mode_secondary + ' border-b border-gray-900'} w-full flex justify-between items-center`}>
                    <div className="flex items-center space-x-3">
                        <div className={`h-12 w-12 rounded-full overflow-hidden border-2 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} shadow-sm`}>
                            <img src={imageUrl} className='w-full h-full object-cover' alt='' />
                        </div>
                        <div>
                            <h1 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'} capitalize`}>{name}</h1>
                            <h2 className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Last seen: 2 hours ago</h2>
                        </div>
                    </div>
                    <div className="flex gap-x-2">
                        <ProfileOptionButton icon={'bi bi-camera-video'} />
                        <ProfileOptionButton icon={'bi bi-telephone'} />
                    </div>
                </div>
                <div className={`flex-1 ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_primary} overflow-y-auto p-4`}>
                    {loading
                        ? <div className="flex justify-center items-center h-full">
                            <div className={`p-4 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-white shadow-sm' : 'bg-gray-800'}`}>
                                <div className="flex items-center space-x-2">
                                    <div className={`animate-spin rounded-full h-5 w-5 border-b-2 ${appMode === AppMode.LIGHT ? 'border-blue-500' : 'border-blue-400'}`}></div>
                                    <p className="font-medium">Loading messages...</p>
                                </div>
                            </div>
                        </div>
                        : error
                            ? <div className="flex justify-center items-center h-full">
                                <div className={`p-4 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-red-50 text-red-600' : 'bg-red-900 text-red-200'}`}>
                                    <p className="font-medium flex items-center">
                                        <i className="bi bi-exclamation-triangle-fill mr-2"></i> Error loading messages. Please try again.
                                    </p>
                                </div>
                            </div>
                            : formattedMessages.length === 0
                                ? <div className="flex justify-center items-center h-full">
                                    <div className={`p-6 rounded-lg text-center`}>
                                        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-500' : 'bg-blue-900 text-blue-300'}`}>
                                            <i className="bi bi-chat-dots text-2xl"></i>
                                        </div>
                                        <p className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>No messages yet.</p>
                                        <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Start a conversation!</p>
                                    </div>
                                </div>
                                : <ul className="p-1.5 mb-0 space-y-1">
                                    {formattedMessages.map((message, index) => (
                                        <li className={`w-full ${message.myMessage ? 'text-end' : ''}`} key={index} onContextMenu={(e) => handleContextMenu(e, message)}>
                                            <div className={`${message.fileUrl === null ? 'px-4 py-3' : ''} relative inline-block max-w-sm ${message.myMessage
                                                ? `bg-blue-600 text-white rounded-tl-xl rounded-tr-xl rounded-bl-xl shadow-sm`
                                                : appMode === AppMode.LIGHT
                                                    ? `bg-white shadow-sm rounded-tl-xl rounded-tr-xl rounded-br-xl border border-gray-100`
                                                    : `${dark_mode_secondary} rounded-tl-xl rounded-tr-xl rounded-br-xl border border-gray-800`
                                                }`}
                                            >
                                                {message.fileUrl && (
                                                    <div className="mb-2">
                                                        <img src={message.fileUrl} className="rounded-lg rounded-b-none max-h-48 object-contain" style={{ display: 'inline-block', maxWidth: '220px' }} jsx={value.toString()} alt="" />
                                                    </div>
                                                )}
                                                <div className={`${message.fileUrl === null ? '' : 'px-4 py-3'}`}>{message.message}</div>
                                            </div>
                                            <div className={`w-full mt-1.5 text-xs flex items-center ${message.myMessage ? 'justify-end' : 'justify-start'} ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {message.time} {message.edited && <span className="text-xs ml-1 opacity-70">(edited)</span>}
                                                {message.myMessage && (
                                                    <span className="ml-2">
                                                        {message.status === 'sent'
                                                            ? <i className="bi bi-check"></i>
                                                            : <i className="bi bi-check2-all text-blue-500"></i>
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </ul>
                    }
                    {contextMenu.visible && <MessageContextMenu appMode={appMode} position={contextMenu} onClose={handleCloseContextMenu} onEdit={handleEditMessage} onDelete={handleDeleteMessage} onReply={handleMessageReply} onReplySuggestion={messageReplySuggestion} />}
                    <ConfirmDialog open={showDeleteConfirm} title="Delete Message" message="Are you sure you want to delete this message? This action cannot be undone." onConfirm={handleConfirmDelete} onCancel={() => { setShowDeleteConfirm(false); setMessageToAction(null); }} appMode={appMode} setShowDeleteConfirm={setShowDeleteConfirm} />
                </div>
                <div className={`p-4 w-full ${appMode === AppMode.LIGHT ? 'bg-white border-t border-gray-300' : dark_mode_secondary + ' border-t border-gray-800'} shadow-sm`}>
                    {editingMessage && (
                        <div className={`mb-2 p-2 rounded-lg flex justify-between items-center ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700' : 'bg-blue-900 text-blue-200'}`}>
                            <span className="text-sm">Editing message</span>
                            <button onClick={() => { setEditingMessage(null); setValue(''); }} className="text-sm hover:underline"> Cancel</button>
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="flex items-center gap-2">
                        <input type="text" className={`px-4 py-3 rounded-lg flex-1 focus:outline-none focus:ring-2 transition-all duration-200 ${appMode === AppMode.LIGHT
                            ? 'bg-gray-50 border border-gray-300 focus:border-blue-400 focus:ring-blue-100'
                            : 'bg-gray-800 border border-gray-700 focus:border-blue-600 focus:ring-blue-900'}`}
                            placeholder={sending ? "Sending..." : editingMessage ? "Edit your message..." : "Type your message here..."} value={value} onChange={(e) => setValue(e.target.value)} disabled={sending}
                        />
                        <button onClick={() => setShowAttachPopup(!showAttachPopup)} type="button" className={`p-3 rounded-lg ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'} transition-colors duration-200`}>
                            <i className="bi bi-paperclip text-lg"></i>
                        </button>
                        {showAttachPopup && (
                            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
                                <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm transition-opacity" onClick={() => { setShowAttachPopup(false); setSelectedFile(null); setAttachMessage(''); }} />
                                <div className={`relative w-full max-w-md mx-auto rounded-xl shadow-2xl border ${appMode === AppMode.LIGHT ? 'bg-white border-gray-200' : 'bg-gray-800 border-gray-700'} p-6 m-4 animate-[fadeIn_0.2s]`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <span className={`font-semibold text-lg ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Attach a file</span>
                                        <button className={`p-2 rounded-full ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700' : 'text-gray-400 hover:bg-gray-700 hover:text-white'} transition-colors`} onClick={() => { setShowAttachPopup(false); setSelectedFile(null); setAttachMessage(''); }}>
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    </div>
                                    {!selectedFile && (
                                        <label className="flex flex-col items-center justify-center cursor-pointer border-2 border-dashed rounded-lg p-6 transition hover:border-blue-400">
                                            <i className="bi bi-upload text-3xl mb-2" />
                                            <span className={`mb-2 text-sm ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>Click to select a file</span>
                                            <input type="file" className="hidden" onChange={e => {
                                                if (e.target.files[0]) setSelectedFile(e.target.files[0]);
                                            }}
                                            />
                                        </label>
                                    )}
                                    {selectedFile && (
                                        <div>
                                            <div className={`flex items-center justify-between mb-3 p-3 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700' : 'bg-blue-900 text-blue-200'}`}>
                                                <div className="flex items-center gap-2">
                                                    <i className="bi bi-file-earmark-text text-xl" />
                                                    <span className="truncate max-w-[160px] font-medium">{selectedFile.name}</span>
                                                    <span className="ml-2 text-xs text-gray-400">{(selectedFile.size / 1024).toFixed(1)} KB</span>
                                                </div>
                                                <button className="ml-2 text-gray-400 hover:text-red-500" onClick={() => setSelectedFile(null)} aria-label="Remove file">
                                                    <i className="bi bi-x-circle"></i>
                                                </button>
                                            </div>
                                            <input type="text" className={`w-full px-4 py-2 mb-3 rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${appMode === AppMode.LIGHT
                                                ? 'bg-gray-50 border border-gray-300 focus:border-blue-400 focus:ring-blue-100'
                                                : 'bg-gray-800 border border-gray-700 focus:border-blue-600 focus:ring-blue-900'}`}
                                                placeholder="Add a message (optional)..."
                                                value={attachMessage}
                                                onChange={e => setAttachMessage(e.target.value)}
                                                disabled={uploading}
                                            />
                                        </div>
                                    )}
                                    <div className="flex justify-end gap-2 mt-2">
                                        <button className={`px-4 py-2 rounded-lg font-medium ${appMode === AppMode.LIGHT ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'} transition`} onClick={() => { setShowAttachPopup(false); setSelectedFile(null); setAttachMessage(''); }} disabled={uploading}>Cancel</button>
                                        <button className={`px-4 py-2 rounded-lg font-medium ${selectedFile && !uploading ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-300 text-white cursor-not-allowed'} transition`} disabled={!selectedFile || uploading} onClick={async () => {
                                            if (!selectedFile) return;
                                            setUploading(true);
                                            try {
                                                const filePath = `chatFiles/${chatId}/${Date.now()}_${selectedFile.name}`;
                                                const fileRef = storageRef(storage, filePath);
                                                await uploadBytes(fileRef, selectedFile);
                                                const downloadURL = await getDownloadURL(fileRef);
                                                await sendMessage(chatId, user, attachMessage, downloadURL, selectedFile.name);
                                                setShowAttachPopup(false);
                                                setSelectedFile(null);
                                                setAttachMessage('');
                                            } catch (err) {
                                                alert("Failed to upload file. Please try again.");
                                            } finally {
                                                setUploading(false);
                                            }
                                        }}>
                                            {uploading ? (
                                                <span className="flex items-center gap-2">
                                                    <svg className="animate-spin h-4 w-4 mr-1 inline" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                    </svg> Uploading...
                                                </span>
                                            ) : (
                                                <span>Send</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <button type="submit" className={`p-3 rounded-lg ${sending ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white transition-colors duration-200 shadow-sm`} disabled={sending}>
                            {sending ?
                                <i className="bi bi-hourglass"></i> :
                                editingMessage ?
                                    <i className="bi bi-check-lg text-lg"></i> :
                                    <i className="bi bi-send text-lg"></i>
                            }
                        </button>
                    </form>
                </div>
            </div>
        );
    }
}